import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, runTransaction } from 'firebase/firestore';
import { db } from '../store/firebase';
import { useData } from '../store/DataContext';
import { BillPayment, BillingAccount, BillingAuditEntry, BillingConfig, BillingPermission, BillingResult, CatalogueItem, ChargeClaim, CreditNote, PendingCharge, DirectSale, ItemStockMove, NumberSeries, RecurringInvoice, Refund, SalesDocument, TaxRate } from '../types/billing';
import { billingRoles, defaultBillingConfig } from '../data/billingDefaults';
import { BCOL, CONFIG_ID, clean, failure, formatNumber, numberRef, readCounter, writeAudit } from '../utils/billingFirestore';
import { DirectSaleInput, recordDirectSale, voidDirectSale } from '../utils/billingSales';
import { describeConfigChanges } from '../utils/billingConfigDiff';
import { DocumentInput, convertDocument, deleteDraft, runRecurring, saveSalesDocument, setDocumentState, voidInvoice } from '../utils/billingDocuments';
import { CreditNoteInput, PaymentInput, RefundInput, applyCredit, issueCreditNote, recordPayment, recordRefund, reversePayment, voidCreditNote } from '../utils/billingCash';
import { collectHospitalCharges } from '../utils/billingCharges';
import { mergeAccounts } from '../utils/billingAccounts';
import { migrateLegacyBilling } from '../utils/billingMigration';
import { makeId, toN } from '../utils/billingMoney';
type BillingContextValue = {
  isReady: boolean;
  config: BillingConfig;
  items: CatalogueItem[];
  accounts: BillingAccount[];
  documents: SalesDocument[];
  payments: BillPayment[];
  creditNotes: CreditNote[];
  refunds: Refund[];
  recurring: RecurringInvoice[];
  claims: ChargeClaim[];
  audit: BillingAuditEntry[];
  pendingCharges: PendingCharge[];
  legacyCounts: {
    invoices: number;
    payments: number;
    quotations: number;
    creditNotes: number;
    salesReceipts: number;
  };
  sales: DirectSale[];
  can: (permission: BillingPermission) => boolean;
  /** Highest line discount % the signed-in user may apply without approval. */
  maxDiscount: number;
  saveConfig: (patch: Partial<BillingConfig>, summary: string) => Promise<BillingResult>;
  saveTaxes: (taxes: BillingConfig['taxes']) => Promise<BillingResult>;
  setNextNumber: (series: NumberSeries, next: number) => Promise<BillingResult>;
  readNextNumber: (series: NumberSeries) => Promise<number>;
  saveItem: (item: Omit<CatalogueItem, 'id' | 'createdAt' | 'updatedAt' | 'priceHistory'> & {
    id?: string;
    openingQuantity?: number;
  }) => Promise<BillingResult>;
  stockMoves: ItemStockMove[];
  /** Receives or adjusts billing-inventory stock. `quantity` is signed for adjustments, positive for receipts. */
  adjustStock: (input: {
    itemId: string;
    type: 'Received' | 'Adjustment';
    quantity: number;
    unitCost?: number;
    reason: string;
    reference?: string;
  }) => Promise<BillingResult>;
  setItemStatus: (id: string, status: CatalogueItem['status']) => Promise<BillingResult>;
  importItems: (rows: (Omit<CatalogueItem, 'id' | 'createdAt' | 'updatedAt' | 'priceHistory'>)[]) => Promise<BillingResult>;
  recordDirectSale: (input: DirectSaleInput) => Promise<BillingResult>;
  voidDirectSale: (id: string, reason: string) => Promise<BillingResult>;
  saveAccount: (account: Omit<BillingAccount, 'id' | 'createdAt' | 'createdByName'> & {
    id?: string;
  }) => Promise<BillingResult>;
  saveDocument: (input: DocumentInput, issue: boolean) => Promise<BillingResult>;
  convert: (sourceId: string, toType: 'invoice' | 'order') => Promise<BillingResult>;
  voidInvoice: (id: string, reason: string) => Promise<BillingResult>;
  deleteDraft: (id: string) => Promise<BillingResult>;
  setDocumentState: (id: string, state: 'Sent' | 'Accepted' | 'Declined' | 'Cancelled', reason?: string) => Promise<BillingResult>;
  recordPayment: (input: PaymentInput) => Promise<BillingResult>;
  applyCredit: (input: {
    sourceType: 'payment' | 'creditNote';
    sourceId: string;
    invoiceId: string;
    amount: number;
  }) => Promise<BillingResult>;
  reversePayment: (id: string, reason: string) => Promise<BillingResult>;
  issueCreditNote: (input: CreditNoteInput) => Promise<BillingResult>;
  voidCreditNote: (id: string, reason: string) => Promise<BillingResult>;
  recordRefund: (input: RefundInput) => Promise<BillingResult>;
  saveRecurring: (schedule: Omit<RecurringInvoice, 'id' | 'runs' | 'createdAt' | 'createdByName' | 'updatedAt'> & {
    id?: string;
  }) => Promise<BillingResult>;
  setRecurringState: (id: string, state: RecurringInvoice['state']) => Promise<BillingResult>;
  runRecurring: (id: string) => Promise<BillingResult>;
  runMigration: () => Promise<BillingResult>;
};
const BillingContext = createContext<BillingContextValue | undefined>(undefined);
const normaliseRate = (t: TaxRate): TaxRate => ({
  ...t,
  code: t.code || t.id.toUpperCase(),
  classification: t.classification ?? (t.rate > 0 ? 'Standard' : 'Exempt'),
  effectiveFrom: t.effectiveFrom || '2000-01-01'
});
export const mergeConfig = (stored: Partial<BillingConfig> | undefined): BillingConfig => ({
  ...defaultBillingConfig,
  ...(stored ?? {}),
  business: {
    ...defaultBillingConfig.business,
    ...(stored?.business ?? {})
  },
  regional: {
    ...defaultBillingConfig.regional,
    ...(stored?.regional ?? {})
  },
  numbering: {
    ...defaultBillingConfig.numbering,
    ...(stored?.numbering ?? {})
  },
  invoiceDefaults: {
    ...defaultBillingConfig.invoiceDefaults,
    ...(stored?.invoiceDefaults ?? {})
  },
  appearance: {
    ...defaultBillingConfig.appearance,
    ...(stored?.appearance ?? {})
  },
  taxes: {
    ...defaultBillingConfig.taxes,
    ...(stored?.taxes ?? {}),
    rates: (stored?.taxes?.rates ?? defaultBillingConfig.taxes.rates).map(normaliseRate)
  },
  catalogue: {
    ...defaultBillingConfig.catalogue,
    ...(stored?.catalogue ?? {})
  },
  discounts: {
    ...defaultBillingConfig.discounts,
    ...(stored?.discounts ?? {})
  },
  sales: {
    ...defaultBillingConfig.sales,
    ...(stored?.sales ?? {})
  },
  receipts: {
    ...defaultBillingConfig.receipts,
    ...(stored?.receipts ?? {})
  },
  statements: {
    ...defaultBillingConfig.statements,
    ...(stored?.statements ?? {})
  },
  email: {
    ...defaultBillingConfig.email,
    ...(stored?.email ?? {})
  },
  recurring: {
    ...defaultBillingConfig.recurring,
    ...(stored?.recurring ?? {})
  },
  permissions: {
    ...defaultBillingConfig.permissions,
    ...(stored?.permissions ?? {})
  },
  migration: stored?.migration ?? defaultBillingConfig.migration
});

/** Billing v2 data for billing staff only (Administrator & Reception). Nothing is subscribed for other roles. */
export function BillingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const data = useData();
  const {
    currentUser
  } = data;
  const allowed = Boolean(currentUser && billingRoles.includes(currentUser.role));
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const [config, setConfig] = useState<BillingConfig>(defaultBillingConfig);
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [storedAccounts, setStoredAccounts] = useState<BillingAccount[]>([]);
  const [documents, setDocuments] = useState<SalesDocument[]>([]);
  const [payments, setPayments] = useState<BillPayment[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [recurring, setRecurring] = useState<RecurringInvoice[]>([]);
  const [claims, setClaims] = useState<ChargeClaim[]>([]);
  const [audit, setAudit] = useState<BillingAuditEntry[]>([]);
  const [sales, setSales] = useState<DirectSale[]>([]);
  const [stockMoves, setStockMoves] = useState<ItemStockMove[]>([]);
  useEffect(() => {
    if (!allowed) return;
    const subscribe = <T,>(name: string, setter: (rows: T[]) => void) => onSnapshot(
    // The audit trail grows forever; only the latest 500 entries are needed on screen.
    name === BCOL.audit ? query(collection(db, name), orderBy('at', 'desc'), limit(500)) : collection(db, name), snap => {
      setter(snap.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as T));
      setLoaded(prev => prev[name] ? prev : {
        ...prev,
        [name]: true
      });
    }, error => {
      console.error(`Billing subscription failed (${name})`, error);
      setLoaded(prev => ({
        ...prev,
        [name]: true
      }));
    });
    const unsubs = [subscribe<CatalogueItem>(BCOL.items, setItems), subscribe<BillingAccount>(BCOL.accounts, setStoredAccounts), subscribe<SalesDocument>(BCOL.documents, setDocuments), subscribe<BillPayment>(BCOL.payments, setPayments), subscribe<DirectSale>(BCOL.sales, setSales), subscribe<ItemStockMove>(BCOL.stockMoves, setStockMoves), subscribe<CreditNote>(BCOL.creditNotes, setCreditNotes), subscribe<Refund>(BCOL.refunds, setRefunds), subscribe<RecurringInvoice>(BCOL.recurring, setRecurring), subscribe<ChargeClaim>(BCOL.claims, setClaims), subscribe<BillingAuditEntry>(BCOL.audit, setAudit), onSnapshot(doc(db, BCOL.config, CONFIG_ID), snap => {
      setConfig(mergeConfig(snap.exists() ? snap.data() as Partial<BillingConfig> : undefined));
      setLoaded(prev => ({
        ...prev,
        config: true
      }));
    }, error => {
      console.error('Billing settings subscription failed', error);
      setLoaded(prev => ({
        ...prev,
        config: true
      }));
    })];
    return () => unsubs.forEach(u => u());
  }, [allowed]);
  const can = (permission: BillingPermission) => Boolean(currentUser && config.permissions[permission]?.includes(currentUser.role));

  /** Every action checks the signed-in role against Billing Settings → Permissions before writing. */
  const actor = (permission: BillingPermission) => {
    if (!currentUser) throw new Error('Please sign in again.');
    if (!config.permissions[permission]?.includes(currentUser.role)) throw new Error('Your role is not permitted to do this. Ask an administrator to change Billing Settings → Permissions.');
    return currentUser;
  };
  const guard = async (permission: BillingPermission, run: (user: NonNullable<typeof currentUser>) => Promise<BillingResult>): Promise<BillingResult> => {
    try {
      return await run(actor(permission));
    } catch (error) {
      return failure(error, 'The action could not be completed.');
    }
  };
  const accounts = useMemo(() => mergeAccounts(storedAccounts, data.patients), [storedAccounts, data.patients]);
  const pendingCharges = useMemo(() => collectHospitalCharges({
    encounters: data.encounters,
    patients: data.patients,
    medicines: data.medicines,
    stockMovements: data.stockMovements,
    items,
    claims
  }), [data.encounters, data.patients, data.medicines, data.stockMovements, items, claims]);
  const maxDiscount = can('approveDiscounts') ? 100 : can('applyDiscounts') ? config.discounts.maxPctWithoutApproval : 0;

  /** Item rules shared by single saves and CSV import. Code uniqueness is re-checked in the transaction. */
  const validateItem = (input: Omit<CatalogueItem, 'id' | 'createdAt' | 'updatedAt' | 'priceHistory'> & {
    id?: string;
  }) => {
    if (!input.code.trim()) return 'Item code is required.';
    if (!/^[A-Za-z0-9._-]+$/.test(input.code.trim())) return 'Item code may only use letters, numbers, dot, dash and underscore.';
    if (!input.name.trim()) return 'Name is required.';
    if (!input.category.trim()) return 'Choose a category.';
    if (!input.department.trim()) return 'Choose a department.';
    if (!input.unit?.trim()) return 'Choose a unit of measure.';
    if (!(Number(input.unitPrice) >= 0) || !Number.isFinite(Number(input.unitPrice))) return 'Enter a valid selling price.';
    if (Math.abs(Math.round(Number(input.unitPrice) * 100) - Number(input.unitPrice) * 100) > 1e-6) return 'Prices can have at most two decimal places.';
    if (!config.taxes.rates.some(t => t.id === input.taxId)) return 'Choose a valid tax code.';
    for (const [label, v] of [['Reorder level', input.reorderLevel], ['Cost price', input.costPrice]] as const) {
      if (v !== undefined && (!Number.isFinite(Number(v)) || Number(v) < 0)) return `${label} cannot be negative.`;
    }
    if (input.reorderLevel !== undefined && !Number.isInteger(Number(input.reorderLevel))) return 'Reorder level must be a whole number.';
    return null;
  };
  const itemCodeRef = (code: string) => doc(db, BCOL.numbers, `itemcode__${code.trim().toLowerCase().replace(/[/\\#?]/g, '_')}`);
  const value: BillingContextValue = {
    isReady: allowed && ['config', BCOL.documents, BCOL.payments, BCOL.items, BCOL.accounts, BCOL.sales].every(k => loaded[k]),
    config,
    items,
    accounts,
    documents,
    payments,
    creditNotes,
    refunds,
    recurring,
    claims,
    audit,
    pendingCharges,
    sales,
    legacyCounts: {
      invoices: data.invoices.length,
      payments: data.payments.length,
      quotations: data.quotations.length,
      creditNotes: data.creditNotes.length,
      salesReceipts: data.salesReceipts.length
    },
    can,
    maxDiscount,
    saveConfig: (patch, summary) => guard('manageSettings', async user => {
      if ('taxes' in patch) return {
        ok: false,
        error: 'Use the Tax settings section to change taxes.'
      };
      let changes: string[] = [];
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.config, CONFIG_ID);
        const snap = await tx.get(ref);
        const current = mergeConfig(snap.exists() ? snap.data() as Partial<BillingConfig> : undefined);
        changes = describeConfigChanges(current, patch);
        if (!changes.length) return;
        tx.set(ref, clean({
          ...current,
          ...patch,
          migration: current.migration,
          updatedAt: new Date().toISOString(),
          updatedByName: user.fullName
        }));
        writeAudit(tx, user, {
          action: 'Settings changed',
          entity: 'Settings',
          entityId: CONFIG_ID,
          summary: `${summary}: ${changes.join('; ')}`.slice(0, 1400)
        });
      });
      return {
        ok: true,
        message: changes.length ? 'Settings saved.' : 'No changes to save.'
      };
    }),
    saveTaxes: taxes => guard('manageTaxes', async user => {
      const codes = new Set<string>();
      for (const t of taxes.rates) {
        if (!t.name.trim() || !t.code?.trim()) return {
          ok: false,
          error: 'Every tax code needs a code and a name.'
        };
        if (codes.has(t.code.trim().toUpperCase())) return {
          ok: false,
          error: `Tax code ${t.code} is used twice.`
        };
        codes.add(t.code.trim().toUpperCase());
        if (!(t.rate >= 0 && t.rate <= 100)) return {
          ok: false,
          error: `${t.name}: rate must be between 0 and 100.`
        };
        if (t.classification !== 'Standard' && t.rate !== 0) return {
          ok: false,
          error: `${t.name}: zero-rated and exempt codes must have a 0% rate.`
        };
        if (t.classification === 'Standard' && t.rate <= 0) return {
          ok: false,
          error: `${t.name}: a standard-rated code needs a rate above 0%.`
        };
        if (!t.effectiveFrom) return {
          ok: false,
          error: `${t.name}: enter the date the rate takes effect.`
        };
      }
      if (!taxes.rates.some(t => t.id === 'exempt')) return {
        ok: false,
        error: 'The Exempt code cannot be removed.'
      };
      const def = taxes.rates.find(t => t.id === taxes.defaultTaxId);
      if (!def?.active) return {
        ok: false,
        error: 'The default tax code must be active.'
      };
      let changes: string[] = [];
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.config, CONFIG_ID);
        const snap = await tx.get(ref);
        const current = mergeConfig(snap.exists() ? snap.data() as Partial<BillingConfig> : undefined);
        changes = describeConfigChanges(current, {
          taxes
        });
        if (!changes.length) return;
        tx.set(ref, clean({
          ...current,
          taxes,
          updatedAt: new Date().toISOString(),
          updatedByName: user.fullName
        }));
        writeAudit(tx, user, {
          action: 'Tax settings changed',
          entity: 'Settings',
          entityId: CONFIG_ID,
          summary: changes.join('; ').slice(0, 1400)
        });
      });
      return {
        ok: true,
        message: changes.length ? 'Tax settings saved. Only new transactions use them — issued records keep their original tax.' : 'No changes to save.'
      };
    }),
    readNextNumber: async series => {
      const last = await runTransaction(db, async tx => (await readCounter(tx, config, series)).last);
      return last + 1;
    },
    setNextNumber: (series, next) => guard('manageSettings', async user => {
      if (!Number.isInteger(next) || next < 1) return {
        ok: false,
        error: 'The next number must be a whole number of 1 or more.'
      };
      const candidate = formatNumber(config, series, next);
      await runTransaction(db, async tx => {
        const counter = await readCounter(tx, config, series);
        const taken = await tx.get(numberRef(series, candidate));
        if (taken.exists()) throw new Error(`${candidate} has already been issued. Choose a number that has not been used.`);
        tx.set(counter.ref, {
          last: next - 1,
          updatedAt: new Date().toISOString()
        });
        writeAudit(tx, user, {
          action: 'Numbering changed',
          entity: 'Settings',
          entityId: CONFIG_ID,
          summary: `${series}: next number ${formatNumber(config, series, counter.last + 1)} → ${candidate}`
        });
      });
      return {
        ok: true,
        message: `The next number will be ${candidate}. Numbers already issued are always skipped.`
      };
    }),
    saveItem: input => guard('manageCatalogue', async user => {
      const problem = validateItem(input);
      if (problem) return {
        ok: false,
        error: problem
      };
      const name = input.name.trim();
      const code = input.code.trim();
      const id = input.id ?? makeId('item');
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.items, id);
        const codeRef = itemCodeRef(code);
        const [snap, codeSnap] = await Promise.all([tx.get(ref), tx.get(codeRef)]);
        const current = snap.data() as CatalogueItem | undefined;
        const owner = codeSnap.data()?.ownerId as string | undefined;
        if (owner && owner !== id || items.some(i => i.code.toLowerCase() === code.toLowerCase() && i.id !== id)) throw new Error(`Item code ${code} is already used.`);
        const now = new Date().toISOString();
        const priceChanged = !current || toN(current.unitPrice) !== toN(input.unitPrice);
        if (current && current.code.toLowerCase() !== code.toLowerCase()) tx.delete(itemCodeRef(current.code));
        tx.set(codeRef, {
          ownerId: id,
          code,
          at: now
        });
        const {
          openingQuantity,
          ...fields
        } = input;
        // Billing inventory is separate from Pharmacy: any previous medicine link is dropped on save.
        fields.medicineId = undefined;
        fields.medicineName = undefined;
        const tracked = fields.kind === 'Stock';
        const opening = !current && tracked ? Math.max(0, Math.floor(Number(openingQuantity) || 0)) : 0;
        if (opening > 0) {
          const moveId = makeId('smove');
          tx.set(doc(db, BCOL.stockMoves, moveId), clean({
            id: moveId,
            itemId: id,
            itemCode: code,
            itemName: name,
            type: 'Opening stock',
            quantity: opening,
            before: 0,
            after: opening,
            unitCost: fields.costPrice,
            byName: user.fullName,
            at: now
          } satisfies ItemStockMove));
        }
        tx.set(ref, clean({
          ...current,
          ...fields,
          quantityOnHand: tracked ? current?.quantityOnHand ?? opening : current?.quantityOnHand,
          id,
          name,
          code,
          unitPrice: Number(input.unitPrice),
          matchNames: input.matchNames.map(m => m.trim()).filter(Boolean),
          priceHistory: priceChanged ? [...(current?.priceHistory ?? []), {
            price: Number(input.unitPrice),
            from: now,
            byName: user.fullName
          }] : current!.priceHistory,
          createdAt: current?.createdAt ?? now,
          updatedAt: now
        }));
        writeAudit(tx, user, {
          action: current ? priceChanged ? 'Price changed' : 'Item updated' : 'Item created',
          entity: 'Item',
          entityId: id,
          reference: code,
          summary: `${name}${priceChanged ? ` — ${current ? `${current.unitPrice.toFixed(2)} → ` : ''}${Number(input.unitPrice).toFixed(2)}` : ''}`
        });
      });
      return {
        ok: true,
        id,
        message: 'Item saved and available for invoices and direct sales. Issued documents keep their original prices.'
      };
    }),
    stockMoves,
    adjustStock: input => guard('manageCatalogue', async user => {
      const qty = Number(input.quantity);
      if (!Number.isInteger(qty) || qty === 0) return {
        ok: false,
        error: 'Enter a whole-number quantity other than zero.'
      };
      if (input.type === 'Received' && qty < 0) return {
        ok: false,
        error: 'Received quantity must be positive.'
      };
      if (!input.reason.trim()) return {
        ok: false,
        error: 'A reason is required for every stock change.'
      };
      if (input.unitCost !== undefined && !(input.unitCost >= 0)) return {
        ok: false,
        error: 'Unit cost cannot be negative.'
      };
      let after = 0;
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.items, input.itemId);
        const item = (await tx.get(ref)).data() as CatalogueItem | undefined;
        if (!item) throw new Error('Item not found.');
        if (item.kind !== 'Stock') throw new Error('This item is a service — change its type to Stock item first.');
        const before = Number(item.quantityOnHand) || 0;
        after = before + qty;
        if (after < 0) throw new Error(`Only ${before} ${item.unit ?? 'unit(s)'} on hand — cannot remove ${Math.abs(qty)}.`);
        const now = new Date().toISOString();
        const moveId = makeId('smove');
        tx.update(ref, clean({
          quantityOnHand: after,
          ...(input.type === 'Received' && input.unitCost !== undefined ? {
            costPrice: input.unitCost
          } : {}),
          updatedAt: now
        }));
        tx.set(doc(db, BCOL.stockMoves, moveId), clean({
          id: moveId,
          itemId: item.id,
          itemCode: item.code,
          itemName: item.name,
          type: input.type,
          quantity: qty,
          before,
          after,
          unitCost: input.unitCost,
          reason: input.reason.trim(),
          reference: input.reference?.trim() || undefined,
          byName: user.fullName,
          at: now
        } satisfies ItemStockMove));
        writeAudit(tx, user, {
          action: input.type === 'Received' ? 'Stock received' : 'Stock adjusted',
          entity: 'Item',
          entityId: item.id,
          reference: item.code,
          summary: `${item.name}: ${before} → ${after}`,
          reason: input.reason.trim()
        });
      });
      return {
        ok: true,
        message: `Stock updated — ${after} now on hand.`
      };
    }),
    setItemStatus: (id, status) => guard('manageCatalogue', async user => {
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.items, id);
        const current = (await tx.get(ref)).data() as CatalogueItem | undefined;
        if (!current) throw new Error('Item not found.');
        tx.update(ref, {
          status,
          updatedAt: new Date().toISOString()
        });
        writeAudit(tx, user, {
          action: status === 'Active' ? 'Item reactivated' : 'Item deactivated',
          entity: 'Item',
          entityId: id,
          reference: current.code,
          summary: current.name
        });
      });
      return {
        ok: true,
        id,
        message: status === 'Active' ? 'Item reactivated.' : 'Item deactivated — it no longer appears when billing. History is kept.'
      };
    }),
    importItems: rows => guard('manageCatalogue', async user => {
      if (!rows.length) return {
        ok: false,
        error: 'The file has no item rows.'
      };
      if (rows.length > 150) return {
        ok: false,
        error: 'Import up to 150 items at a time.'
      };
      const seen = new Set<string>();
      for (const [i, row] of rows.entries()) {
        const problem = validateItem(row);
        if (problem) return {
          ok: false,
          error: `Row ${i + 2}: ${problem}`
        };
        const key = row.code.trim().toLowerCase();
        if (seen.has(key)) return {
          ok: false,
          error: `Row ${i + 2}: code ${row.code} appears twice in the file.`
        };
        seen.add(key);
        if (items.some(it => it.code.toLowerCase() === key)) return {
          ok: false,
          error: `Row ${i + 2}: code ${row.code} already exists. Edit it in Items & Services instead.`
        };
      }
      await runTransaction(db, async tx => {
        const codeSnaps = await Promise.all(rows.map(r => tx.get(itemCodeRef(r.code))));
        const clash = codeSnaps.findIndex(s => s.exists());
        if (clash >= 0) throw new Error(`Code ${rows[clash].code} was added by someone else. Nothing was imported.`);
        const now = new Date().toISOString();
        rows.forEach(row => {
          const id = makeId('item');
          tx.set(itemCodeRef(row.code), {
            ownerId: id,
            code: row.code.trim(),
            at: now
          });
          tx.set(doc(db, BCOL.items, id), clean({
            ...row,
            id,
            code: row.code.trim(),
            name: row.name.trim(),
            priceHistory: [{
              price: row.unitPrice,
              from: now,
              byName: user.fullName
            }],
            createdAt: now,
            updatedAt: now
          }));
        });
        writeAudit(tx, user, {
          action: 'Items imported',
          entity: 'Item',
          entityId: 'import',
          summary: `${rows.length} item(s) imported from CSV`
        });
      });
      return {
        ok: true,
        message: `${rows.length} item(s) imported.`
      };
    }),
    recordDirectSale: input => guard('directSales', user => recordDirectSale(user, config, input, {
      canEditPrices: can('editPrices'),
      maxDiscount,
      items,
      batches: []
    })),
    voidDirectSale: (id, reason) => guard('voids', user => voidDirectSale(user, id, reason)),
    saveAccount: input => guard('createDocuments', async user => {
      const name = input.name.trim();
      if (!name) return {
        ok: false,
        error: 'Account name is required.'
      };
      if (input.type !== 'Patient' && accounts.some(a => a.type === input.type && a.name.toLowerCase() === name.toLowerCase() && a.id !== input.id)) return {
        ok: false,
        error: `A ${input.type.toLowerCase()} account called ${name} already exists.`
      };
      const id = input.id ?? `acct-${input.type.toLowerCase()}-${makeId('a').slice(2)}`;
      const now = new Date().toISOString();
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.accounts, id);
        const snap = await tx.get(ref);
        const current = snap.data() as BillingAccount | undefined;
        tx.set(ref, clean({
          ...current,
          ...input,
          id,
          name,
          createdAt: current?.createdAt ?? now,
          createdByName: current?.createdByName ?? user.fullName,
          updatedAt: now
        }));
        writeAudit(tx, user, {
          action: current ? 'Account updated' : 'Account created',
          entity: 'Account',
          entityId: id,
          summary: `${input.type}: ${name}`
        });
      });
      return {
        ok: true,
        id,
        message: 'Account saved.'
      };
    }),
    saveDocument: (input, issue) => guard(issue ? 'issueInvoices' : 'createDocuments', async user => {
      const overLimit = input.lines.find(l => !l.fixed && l.discountPct > maxDiscount);
      if (overLimit) return {
        ok: false,
        error: maxDiscount === 0 ? 'Your role cannot apply discounts.' : `"${overLimit.description}": discounts above ${maxDiscount}% need approval from a user with discount-approval permission.`
      };
      if (!can('editPrices')) {
        const changed = input.lines.find(l => l.itemId && !l.fixed && items.find(i => i.id === l.itemId && i.unitPrice !== l.unitPrice));
        if (changed) return {
          ok: false,
          error: `Your role cannot change catalogue prices ("${changed.description}").`
        };
        const unpriced = input.lines.find(l => !l.fixed && !l.itemId && !l.sourceRef && l.unitPrice > 0);
        if (unpriced) return {
          ok: false,
          error: `"${unpriced.description || 'Free line'}" is not in Items & Services. Your role can only bill catalogue prices.`
        };
      }
      return saveSalesDocument(user, config, input, issue);
    }),
    convert: (sourceId, toType) => guard('createDocuments', user => convertDocument(user, config, sourceId, toType)),
    voidInvoice: (id, reason) => guard('voids', user => voidInvoice(user, id, reason)),
    deleteDraft: id => guard('createDocuments', user => deleteDraft(user, id)),
    setDocumentState: (id, state, reason) => guard('createDocuments', user => setDocumentState(user, id, state, reason)),
    recordPayment: input => guard('recordPayments', user => recordPayment(user, config, input)),
    applyCredit: input => guard('issueCredits', user => applyCredit(user, input)),
    reversePayment: (id, reason) => guard('voids', user => reversePayment(user, id, reason)),
    issueCreditNote: input => guard('issueCredits', user => issueCreditNote(user, config, input)),
    voidCreditNote: (id, reason) => guard('voids', user => voidCreditNote(user, id, reason)),
    recordRefund: input => guard('refunds', user => recordRefund(user, config, input)),
    saveRecurring: input => guard('createDocuments', async user => {
      if (!input.name.trim()) return {
        ok: false,
        error: 'Give the schedule a name.'
      };
      if (!input.lines.length) return {
        ok: false,
        error: 'Add at least one item.'
      };
      if (input.endDate && input.endDate < input.nextRun) return {
        ok: false,
        error: 'The end date must be after the next run date.'
      };
      const id = input.id ?? makeId('rec');
      const now = new Date().toISOString();
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.recurring, id);
        const current = (await tx.get(ref)).data() as RecurringInvoice | undefined;
        tx.set(ref, clean({
          ...current,
          ...input,
          id,
          runs: current?.runs ?? [],
          createdAt: current?.createdAt ?? now,
          createdByName: current?.createdByName ?? user.fullName,
          updatedAt: now
        }));
        writeAudit(tx, user, {
          action: current ? 'Recurring schedule updated' : 'Recurring schedule created',
          entity: 'Recurring',
          entityId: id,
          summary: `${input.name} — ${input.frequency}, next ${input.nextRun}`
        });
      });
      return {
        ok: true,
        id,
        message: 'Schedule saved.'
      };
    }),
    setRecurringState: (id, state) => guard('createDocuments', async user => {
      await runTransaction(db, async tx => {
        const ref = doc(db, BCOL.recurring, id);
        const current = (await tx.get(ref)).data() as RecurringInvoice | undefined;
        if (!current) throw new Error('Schedule not found.');
        tx.update(ref, {
          state,
          updatedAt: new Date().toISOString()
        });
        writeAudit(tx, user, {
          action: `Recurring schedule ${state.toLowerCase()}`,
          entity: 'Recurring',
          entityId: id,
          summary: current.name
        });
      });
      return {
        ok: true,
        id
      };
    }),
    runRecurring: id => guard('createDocuments', user => runRecurring(user, config, id)),
    runMigration: () => guard('manageSettings', async user => {
      if (config.migration.status === 'completed') return {
        ok: false,
        error: 'Migration has already been completed.'
      };
      if (!loaded[BCOL.config] && !loaded.config) return {
        ok: false,
        error: 'Settings are still loading.'
      };
      const report = await migrateLegacyBilling(user, config, {
        invoices: data.invoices,
        quotations: data.quotations,
        payments: data.payments,
        creditNotes: data.creditNotes,
        salesReceipts: data.salesReceipts,
        walkInClients: data.walkInClients,
        billingItems: data.billingItems,
        patients: data.patients
      }, {
        documents: new Set(documents.map(d => d.id)),
        payments: new Set(payments.map(p => p.id)),
        creditNotes: new Set(creditNotes.map(c => c.id)),
        accounts: new Set(storedAccounts.map(a => a.id)),
        items: new Set(items.map(i => i.id))
      });
      return {
        ok: true,
        message: report.mismatches.length ? `Migration completed with ${report.mismatches.length} balance difference(s) to review.` : 'Migration completed and reconciled.'
      };
    })
  };
  return <BillingContext.Provider value={value} data-id="element-129" data-mp="r6ien3tzzwpu">{children}</BillingContext.Provider>;
}
export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) throw new Error('useBilling must be used within BillingProvider');
  return context;
}